/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { range } from 'lodash-es';
import React, {useEffect} from 'react';
import { useForm } from 'react-hook-form';
import {
  HSUser,
  SyringeDriverRunActivityDto, SyringeDriverRunDto, SyringeDriverRunScheduledActivityDto, SyringeDriverRunStatusDto
} from 'server-openapi';
import styled from 'styled-components';
import { useCurrentUser } from '../../../../core/authn/UserProvider';
import { DateUtils } from '../../../../core/utils/dateUtils';
import { Button } from '../../../../kit/Button';
import { Dialog } from '../../../../kit/Dialog';
import { Form } from '../../../../kit/Forms/Form';
import { FormGroup, FormLabel } from "../../../../kit/Forms/FormGroup";
import { RadioGroup } from '../../../../kit/Forms/RadioGroup';
import { Select } from '../../../../kit/Forms/Select';
import { TextArea } from '../../../../kit/Forms/TextArea';
import {TextInput} from '../../../../kit/Forms/TextInput';
import { Grid } from '../../../../kit/Grid';
import { Layout } from '../../../../kit/Layout';
import { Text } from '../../../../kit/Text';
import { toasts } from '../../../../kit/Toasts/Toaster';
import { useSyncCenter } from '../../../../syncstream/SyncCenterProvider';
import { useRoundSchedule } from '../../../Rounds/services/RoundScheduleProvider';
import {
  MedicationInformationBox,
  MedicationInformationProps,
} from '../medicationInformation/MedicationInformationBox';
import {useGroupPermissions} from "../../../../core/authz/PermissionsProvider";
import {SyncSyringeDriverRuns} from "../../../../syncstream/SyncSyringeDriverRun";
import {SyringeDriverRunActivityTypeDto} from "server-openapi/dist/generated-sdk/api";
import {AdministrationPermissions, SecondCheckData} from "../MedicationListsTabbedRouter/MedicationListsTabbedRouter";
import {addSeconds, differenceInMilliseconds} from "date-fns";
import {DateInput} from "../../../../kit/Forms/DateInput";
import {syringeDriverSiteLocations} from "./SyringeDriverRunAdministrationDialog";
import {useMap} from "../../../../core/storage/hooks/UseStore";

interface Props extends MedicationInformationProps {
  
  syringeDriverRun: SyringeDriverRunDto;
  performedActivity?: SyringeDriverRunActivityDto;
  scheduledActivity?: SyringeDriverRunScheduledActivityDto;
  unscheduledActivity?: SyringeDriverRunActivityDto;
  open: boolean;
  setOpen: (open: boolean) => void;
  activityKind: SyringeDriverRunActivityTypeDto;
  administrationPermissions: AdministrationPermissions;
  secondCheckData?: SecondCheckData;
}

export function SyringeDriverRunActivityDialog(props: Props) {
  if (props.open && !props.performedActivity && !props.administrationPermissions.canOperateSyringeDriver) {
    toasts.error('You do not have permission to operate syringe driver');
    props.setOpen(false);
  }
  return (
    <Dialog lazy closeButtonColor={'#fff'} open={props.open} onRequestClose={() => props.setOpen(false)} size="full">
      <DialogContainer horizontal gap={2} data-testid="medicine-administer-dialog">
        <SyringeDriverRunActivityForm {...props} />
      </DialogContainer>
    </Dialog>
  );
}

interface FormFields {
  siteLocation?: string;
  siteLocationOther?: string;
  siteStatus?: string;
  lineStatus?: string;
  lineClampingOn?: string;
  pumpInfusing?: string;
  batteryLevel?: number;
  notes?: string;
  volumeTotal?: number | null;
  volumeInfused?: number | null;
  volumeToBeInfused?: string | null;
  rateOfInfusion?: number | null;
  stopReason?: SyringeDriverRunActivityTypeDto;
  startDate?: Date;
  endDate?: Date;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function SyringeDriverRunActivityForm(props: Props) {
  const services = useSyncCenter();
  const groupPermissions = useGroupPermissions();

  const canRecordNote = groupPermissions.canRecordNote;
  const user = useCurrentUser();
  const hsUserMap = useMap(services.users);
  const hsUser = hsUserMap.get(user.profile.sub) ?? {
    subjectId: user.profile.sub,
    firstName: "",
    familyName: ""  
  };
  const roundSchedule = useRoundSchedule();
  const activity = props.performedActivity;

  const isRestarting = props.activityKind === SyringeDriverRunActivityTypeDto.Restart;
  const isStoppingOrPausing = props.activityKind === SyringeDriverRunActivityTypeDto.Stop;

  function handleRateChange() {
    const rate = form.getValues().rateOfInfusion;
    const total = form.getValues().volumeTotal;
    const sofar = form.getValues().volumeInfused;
    
    if (rate && rate > 0 && total && total > 0 && sofar && sofar > 0) {
      const remaining = Math.round(Math.max(0, total - sofar) * 100) / 100;
      const remainingSeconds = Math.min(48 * 3600, remaining / rate * 3600);
      const endAt = addSeconds(new Date(), remainingSeconds);  
      
      form.setValue("endDate", endAt);
      form.setValue("volumeToBeInfused", remaining);
    }
  }
  function twoDecimalPlaces(value: number): number {
    return Math.round(value * 100) / 100; 
  }

  function timerEvent() {
    if (!!props.performedActivity) {
      // Ignore when we are disp[la
      return;
    }
    if (props.syringeDriverRun.status === SyringeDriverRunStatusDto.Stopped || props.syringeDriverRun.status === SyringeDriverRunStatusDto.Paused) {
      handleRateChange();
    }
    
  }
  useEffect(() => {
    const interval = setInterval(() => {
      timerEvent();
    }, 1000 * 60);

    return () => clearInterval(interval); // This represents the unmount function, in which you need to clear your interval to prevent memory leaks.
  }, []);

  useEffect(() => {
    timerEvent();
  }, []);
  
  function calculateAmountUsed(rate: number | undefined | null, start_at: Date | undefined | null) {
    if (!rate || !start_at) {
      return 0;
    }
    if (rate === 0) {
      return 0;
    }
    // Convert milliseconds, multiply by 100, round and then divide by 1000 to get decimal hours to 2 decimal places.
    return twoDecimalPlaces(differenceInMilliseconds(new Date(), start_at) * rate / (3600 * 1000));  
  }
  
  
  let amtUsed: number | undefined | null;
   if (activity && activity.infusion) {
    amtUsed = activity.infusion.amountAdministeredMl;
  }
  else if (props.syringeDriverRun.status === SyringeDriverRunStatusDto.Stopped || props.syringeDriverRun.status === SyringeDriverRunStatusDto.Paused) {
    amtUsed = props.syringeDriverRun.infusion?.amountAdministeredMl;
  }
  else {
     // Find the last activity and calculate the amount since then.
     // E.g. if we started out with 10ml, and last time we looked it was 8ml, then calculate the amt used by from the last time we looked and 8ml. 
    amtUsed = (props.syringeDriverRun.infusion?.amountAdministeredMl ?? 0) + calculateAmountUsed(props.syringeDriverRun.infusion?.rateMlPerH, DateUtils.toDate(props.syringeDriverRun.clientUpdatedAt!));
    if (props.syringeDriverRun.infusion?.amountTotalMl) {
      amtUsed = Math.min(amtUsed, props.syringeDriverRun.infusion.amountTotalMl!);
    }
  }

  const amtTotal = activity ? activity.infusion?.amountTotalMl : props.syringeDriverRun?.infusion?.amountTotalMl;
  const amtRemaining = activity ? activity.infusion?.amountRemainingMl : amtUsed && amtTotal ? twoDecimalPlaces(amtTotal - amtUsed) : undefined;
  const sitePosition = activity?.site?.position ?? props.syringeDriverRun?.site?.position ?? undefined;
  const form = useForm<FormFields>({
    defaultValues: {
      stopReason: props.activityKind ?? undefined,
      siteLocation: !!sitePosition ? syringeDriverSiteLocations.includes(sitePosition) ? sitePosition : 'Other' : undefined,  
      siteLocationOther: sitePosition,
      startDate: (props.syringeDriverRun?.startedAt ? DateUtils.toDate(props.syringeDriverRun?.startedAt) : undefined),
      endDate: activity?.scheduledEnd ? DateUtils.toDate(activity.scheduledEnd) : (props.syringeDriverRun?.scheduledEnd ? DateUtils.toDate(props.syringeDriverRun?.scheduledEnd) : undefined), 
      
      siteStatus:
        activity?.site?.condition
            ? activity.site.condition
            : props.syringeDriverRun?.site?.condition
                ? props.syringeDriverRun.site.condition
                : (props.activityKind === SyringeDriverRunActivityTypeDto.Stop
                    ? 'NAD'
                    : 'New'),
      lineStatus:
          activity?.line?.condition
              ? activity.line.condition
              : props.syringeDriverRun?.line?.condition
                  ? props.syringeDriverRun.line.condition
                  : (props.activityKind === SyringeDriverRunActivityTypeDto.Stop
                      ? 'NAD'
                      : 'New'),
      // boolean values (particularly false) don't work well with form inputs, so we map to and from a boolean
      lineClampingOn:
          activity?.statusCheck?.lineClamp
              ? activity.statusCheck.lineClamp
              : props.syringeDriverRun?.statusCheck?.lineClamp
                  ? props.syringeDriverRun.statusCheck.lineClamp
                  : undefined,
      pumpInfusing:
          activity?.statusCheck?.pumpInfusing
              ? activity.statusCheck.pumpInfusing
              : props.syringeDriverRun?.statusCheck?.pumpInfusing
                  ? props.syringeDriverRun.statusCheck.pumpInfusing
                  : undefined,
      batteryLevel:
          activity?.statusCheck?.batteryPercentage
              ? activity.statusCheck.batteryPercentage
              : props.syringeDriverRun?.statusCheck?.batteryPercentage
                  ? props.syringeDriverRun.statusCheck.batteryPercentage
                  : 100,

      notes: activity?.comment ?? undefined,
      volumeTotal: activity?.infusion?.amountTotalMl ?? props.syringeDriverRun?.infusion?.amountTotalMl ?? undefined,
      rateOfInfusion: activity?.infusion?.rateMlPerH ?? props.syringeDriverRun?.infusion?.rateMlPerH ?? undefined,
      volumeInfused: amtUsed ? twoDecimalPlaces(amtUsed) : undefined,
      volumeToBeInfused: amtRemaining?.toString(),
    },
  });

  async function onSubmit(data: FormFields) {
    if (data.notes && !canRecordNote) {
      toasts.error('You do not have permission to record notes');
      return;
    }
    await processActivity(data, props, hsUser, services.syringeDriverRun.service);
    // Refresh round schedule
    // Not su
    if (props.currentRound) {
      roundSchedule.set({
        ...props.currentRound,
        version: (props.currentRound.version ?? 0) + 1,
      });
    }
    props.setOpen(false);
  }

  return (
    <Form form={form} onSubmit={onSubmit}>
      <Layout gap={1}>
        <WhiteText size="large">Update Syringe Driver Run</WhiteText>
        <MedicationInformationBox
            displayCurrentStatusOnly {...props}
            drugImageMaxWidth="240px"
        />
        {props.activityKind === SyringeDriverRunActivityTypeDto.Stop && (
          <FormGroup fullWidth label="Status">
            <RadioGroup
              name="stopReason"
              options={stopOptions}
              required
              disabled={!!props.performedActivity}
              cols={3}
            />
          </FormGroup>
        )}
        <ConsistentGrid cols={2} alignItemsCenter>
          <FormGroup fullWidth label="Total volume to be infused (ml)">
            <TextInput
                type="number"
                name="volumeTotal"
                placeholder="Volume total (ml)"
                fullWidth
                disabled={true}
            />
          </FormGroup>
          <FormGroup fullWidth isRequired label="Rate of infusion (ml/hr)">
            <TextInput
                name="rateOfInfusion"
                type="number"
                placeholder="Rate of infusion in ml/hr"
                required
                disabled={true}
                fullWidth
                step="0.01"
            />
          </FormGroup>
          <FormGroup fullWidth isRequired label="Volume infused thus far (ml)">
            <TextInput
              name="volumeInfused"
              type="number"
              placeholder="Volume infused"
              required
              disabled={true}
              onChange={handleRateChange}
              fullWidth
              step="0.01"
              rules={{
                validate: (soFar:number) => {
                  if (soFar < 0) {
                    return 'Volume infused must be >= 0';
                  }
                  else if (amtTotal && soFar > amtTotal) {
                    return 'Volume infused must be less than or equal to the total infused';
                  }

                  return true;
                },
              }}
            />
          </FormGroup>
          <FormGroup fullWidth isRequired label="Volume yet to be infused (ml)">
            <TextInput
              name="volumeToBeInfused"
              type="number"
              placeholder="Volume to be infused"
              required
              disabled={true}
              defaultValue="0.0"
              fullWidth
              step="0.01"
            />
          </FormGroup>
          <FormGroup label="Start date and time" isRequired fullWidth>
            <DateInput
                name="startDate"
                dateTime
                fullWidth
                required
                disabled={true}
            />
          </FormGroup>
          <FormGroup label="End date and time" isRequired fullWidth>
            <DateInput
                name="endDate"
                dateTime
                fullWidth
                disabled={true}
                required
            />
          </FormGroup>
          
          <FormGroup label="Site Location" isRequired fullWidth>
            <Select
                name="siteLocation"
                options={syringeDriverSiteLocations.map((option) => ({label: option, value: option}))}
                fullWidth
                required
                disabled={!isRestarting}
            />
            {form.watch('siteLocation') === 'Other' && (
                <TextInput
                    name="siteLocationOther"
                    placeholder="Other site location"
                    fullWidth
                    required
                    disabled={!isRestarting}
                />
            )}
          </FormGroup>
          <FormGroup fullWidth label="Site Status">
            <Select
                name="siteStatus"
                options={siteStatusOptions.map((option) => ({ label: option, value: option }))}
                required
                disabled={!!props.performedActivity}
                fullWidth
            />
          </FormGroup>
          <FormGroup fullWidth label="Line Status">
            <Select
                name="lineStatus"
                options={lineStatusOptions.map((option) => ({ label: option, value: option }))}
                required
                disabled={!!props.performedActivity}
                fullWidth
            />
          </FormGroup>
          <FormGroup isRequired fullWidth label="Line clamping on?">
            <Select
                name="lineClampingOn"
                options={booleanOptions.map((option) => ({ label: option, value: option }))}
                required
                disabled={!!props.performedActivity}
                fullWidth
            />
          </FormGroup>
          <FormGroup isRequired fullWidth label="Pump infusing?">
            <Select
                name="pumpInfusing"
                options={booleanOptions.map((option) => ({ label: option, value: option }))}
                required
                disabled={!!props.performedActivity}
                fullWidth
            />
          </FormGroup>
          <FormGroup fullWidth label="Battery level">
            <Select
                name="batteryLevel"
                options={range(0, 11).map((num) => ({ label: `${num * 10}%`, value: num * 10 }))}
                required
                disabled={!!props.performedActivity}
                fullWidth
            />
          </FormGroup>
        </ConsistentGrid>
        <FormGroup fullWidth>
          <TextArea
            name="notes"
            placeholder="Add note"
            rows={3}
            fullWidth
            required={activity?.activityType === SyringeDriverRunActivityTypeDto.Stop}
            disabled={!!props.performedActivity}
          />
        </FormGroup>
        <Button type="submit" fullWidth disabled={!!props.performedActivity} >
          UPDATE STATUS
        </Button>
      </Layout>
    </Form>
  );
}

const DialogContainer = styled(Layout)`
  padding: 1em;
  grid-template-columns: 8fr;
`;

const WhiteText = styled(Text)`
  color: white;
`;

const ConsistentGrid = styled(Grid)`
    align-items: start;

    & ${FormLabel} {
    min-height: 1.5em;
    align-items: flex-start;
  }
`;

// eslint-disable-next-line sonarjs/cognitive-complexity
async function processActivity(
  data: FormFields,
  props: Props,
  user: HSUser,
  syringeDriverRunService: SyncSyringeDriverRuns,
) {
  let endDate = props.syringeDriverRun.scheduledEnd;
  if (data.endDate) {
    endDate = DateUtils.toOffsetlessString(data.endDate)
  }

  const now = new Date();

  const activity: SyringeDriverRunActivityDto = {
    activityType: data.stopReason ?? props.activityKind,
    infusion: {
      rateMlPerH: data.rateOfInfusion,
      amountRemainingMl: +data.volumeToBeInfused!,
      amountAdministeredMl: data.volumeInfused,  
      amountTotalMl: data.volumeTotal
    }, 
    statusCheck: {
      lineClamp: data.lineClampingOn,
      pumpInfusing: data.pumpInfusing,
      batteryPercentage: data.batteryLevel
    }, 
    site: {
      position: data.siteLocation == 'Other' ? data.siteLocationOther :data.siteLocation,
      condition: data.siteStatus, 
    }, 
    clientUser: {
      subjectId: user.subjectId, 
      firstName: user.firstName,
      familyName: user.familyName, 
      designation: user.designation
    }, 
    line: {
      condition: data.lineStatus
    }, 
    comment: data.notes, 
    scheduledActivityId: props.scheduledActivity?.hsId, 
    scheduledEnd: endDate,
    clientCreatedAt: DateUtils.fromDate(now),
    clientUpdatedAt: DateUtils.fromDate(now),
  };
  
  
  await syringeDriverRunService.enqueue.operateSyringeDriverRun(props.syringeDriverRun, activity, user.subjectId!);
}

const siteStatusOptions = ['New', 'NAD', 'Red', 'Inflamed/Swollen', 'Leakage'];

const lineStatusOptions = ['New', 'NAD', 'Crystallised/Cloudy'];

const booleanOptions = ['Yes', 'No'];

const stopOptions = [
  {
    value: SyringeDriverRunActivityTypeDto.Stop,
    label: 'Stop',
  },
  {
    value: SyringeDriverRunActivityTypeDto.Pause,
    label: 'Pause',
  },
];
